import React, { useState, useEffect } from 'react';
import { Box, Paper, Container, Snackbar, Alert, Button, Typography } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import StoreFormStepper from './StoreFormStepper';
import StoreBasicInfoForm from './StoreBasicInfoForm';
import ProductsForm from './ProductsForm';
import PaymentConfigForm from './PaymentConfigForm';
import StorePageForm from './StorePageForm';
import StorePreview from './StorePreview';

import { StoreFormData, StoreFormStep } from './types/store.types';

// Esquemas de validação por etapa
const basicInfoSchema = yup.object().shape({
  name: yup.string().required('O nome da loja é obrigatório').min(3, 'O nome deve ter pelo menos 3 caracteres'),
  slug: yup
    .string()
    .required('O slug é obrigatório')
    .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use apenas letras minúsculas, números e hífens'),
  description: yup.string().required('A descrição é obrigatória').min(20, 'A descrição deve ter pelo menos 20 caracteres'),
  category: yup.string().required('A categoria é obrigatória'),
  location: yup.string().required('O local é obrigatório'),
  address: yup.string().required('O endereço é obrigatório'),
  city: yup.string().required('A cidade é obrigatória'),
  state: yup.string().required('O estado é obrigatório'),
  zipCode: yup.string().required('O CEP é obrigatório'),
  contactEmail: yup.string().email('Email inválido').required('O email de contato é obrigatório'),
  contactPhone: yup.string().required('O telefone de contato é obrigatório'),
});

const productsSchema = yup.object().shape({
  products: yup.array().of(
    yup.object().shape({
      name: yup.string().required('O nome do produto é obrigatório'),
      description: yup.string().required('A descrição é obrigatória'),
      price: yup.number().min(0, 'O preço não pode ser negativo').required('O preço é obrigatório'),
      quantity: yup.number().min(0, 'A quantidade não pode ser negativa').required('A quantidade é obrigatória'),
      category: yup.string().required('A categoria é obrigatória'),
    })
  ).min(1, 'Deve haver pelo menos um produto'),
});

const paymentConfigSchema = yup.object().shape({
  paymentMethods: yup.array().min(1, 'Selecione pelo menos um método de pagamento'),
  minimumOrderValue: yup.number().min(0, 'O valor mínimo não pode ser negativo'),
  freeShippingValue: yup.number().min(0, 'O valor para frete grátis não pode ser negativo'),
});

const storePageSchema = yup.object().shape({
  customTheme: yup.object().shape({
    primaryColor: yup.string().required('A cor primária é obrigatória'),
    secondaryColor: yup.string().required('A cor secundária é obrigatória'),
    fontFamily: yup.string().required('A fonte é obrigatória'),
  }),
});

// Esquema completo (todas as etapas)
const fullSchema = yup.object().shape({
  ...basicInfoSchema.fields,
  ...productsSchema.fields,
  ...paymentConfigSchema.fields,
  ...storePageSchema.fields,
});

// Configuração das etapas
const steps: StoreFormStep[] = [
  {
    id: 0,
    title: 'Informações Básicas',
    description: 'Configure os dados básicos da sua loja',
    component: StoreBasicInfoForm,
    validation: basicInfoSchema,
  },
  {
    id: 1,
    title: 'Produtos',
    description: 'Adicione os produtos da sua loja',
    component: ProductsForm,
    validation: productsSchema,
  },
  {
    id: 2,
    title: 'Pagamento',
    description: 'Configure os métodos de pagamento',
    component: PaymentConfigForm,
    validation: paymentConfigSchema,
  },
  {
    id: 3,
    title: 'Personalização',
    description: 'Personalize a aparência da sua loja',
    component: StorePageForm,
    validation: storePageSchema,
  },
];

interface StoreFormContainerProps {
  initialData?: Partial<StoreFormData>;
  onSubmit: (data: StoreFormData) => void;
  onSaveDraft?: (data: StoreFormData) => void;
}

const StoreFormContainer: React.FC<StoreFormContainerProps> = ({ 
  initialData, 
  onSubmit,
  onSaveDraft 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [autoSave, setAutoSave] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [slug, setSlug] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // Valores padrão para o formulário
  const defaultValues: StoreFormData = {
    name: '',
    slug: '',
    description: '',
    category: 'general',
    location: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    
    products: [
      {
        name: 'Produto Exemplo',
        description: 'Descrição do produto exemplo',
        price: 0,
        originalPrice: 0,
        quantity: 1,
        category: 'general',
        tags: [],
        images: [],
        isActive: true,
        featured: false,
      }
    ],
    
    paymentMethods: ['credit_card', 'pix'],
    allowInstallments: false,
    maxInstallments: 12,
    minimumOrderValue: 0,
    freeShippingValue: 100,
    
    coverImage: null,
    galleryImages: [],
    logo: null,
    showMap: true,
    showSocialShare: true,
    customTheme: {
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      fontFamily: 'Roboto'
    },
    
    status: 'draft'
  };

  // Configurar react-hook-form
  const methods = useForm<StoreFormData>({
    resolver: yupResolver(fullSchema) as any,
    defaultValues: initialData || defaultValues,
    mode: 'onChange',
  });

  const { watch, setValue, getValues, trigger } = methods;

  // Auto-save quando os dados mudam
  useEffect(() => {
    if (autoSave && activeStep > 0) {
      const subscription = watch((data) => {
        if (onSaveDraft) {
          setTimeout(() => {
            onSaveDraft({...data, status: 'draft'} as StoreFormData);
          }, 2000); // Debounce de 2 segundos
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [watch, autoSave, activeStep, onSaveDraft]);

  // Verificar disponibilidade do slug
  const checkSlugAvailability = async (slugToCheck: string) => {
    if (!slugToCheck || slugToCheck.length < 3) return;
    
    setChecking(true);
    setSlugError(null);
    
    try {
      // Aqui você faria uma chamada para a API para verificar se o slug está disponível
      // const response = await storeService.checkSlugAvailability(slugToCheck);
      
      // Simulação de verificação
      const isAvailable = Math.random() > 0.3; // 70% de chance de estar disponível
      
      if (!isAvailable) {
        setSlugError('Este slug já está sendo usado');
      }
    } catch (error) {
      setSlugError('Erro ao verificar disponibilidade do slug');
    } finally {
      setChecking(false);
    }
  };

  // Gerar slug automaticamente baseado no nome
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
      .replace(/\s+/g, '-') // Substitui espaços por hífens
      .replace(/-+/g, '-') // Remove hífens duplicados
      .trim();
  };

  // Observar mudanças no nome para gerar slug automaticamente
  const watchedName = watch('name');
  useEffect(() => {
    if (watchedName && !slug) {
      const generatedSlug = generateSlug(watchedName);
      setSlug(generatedSlug);
      setValue('slug', generatedSlug);
    }
  }, [watchedName, slug, setValue]);

  // Observar mudanças no slug
  const watchedSlug = watch('slug');
  useEffect(() => {
    if (watchedSlug && watchedSlug !== slug) {
      setSlug(watchedSlug);
      checkSlugAvailability(watchedSlug);
    }
  }, [watchedSlug, slug]);

  const handleNext = async () => {
    const currentStepConfig = steps[activeStep];
    const fieldKeys = Object.keys(currentStepConfig.validation.fields) as Array<keyof StoreFormData>;
    const isValid = await trigger(fieldKeys);
    
    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      setSnackbar({
        open: true,
        message: 'Por favor, corrija os erros antes de continuar',
        severity: 'error'
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSaveDraft = () => {
    if (onSaveDraft) {
      const currentData = methods.getValues();
      onSaveDraft({...currentData, status: 'draft'} as StoreFormData);
      setSnackbar({
        open: true,
        message: 'Rascunho salvo com sucesso',
        severity: 'success'
      });
    }
  };

  const handleSubmit = methods.handleSubmit((data) => {
    console.log('[StoreFormContainer] Dados da loja:', data);
    onSubmit(data as StoreFormData);
    setSnackbar({
      open: true,
      message: 'Loja criada com sucesso!',
      severity: 'success'
    });
  });

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const togglePreviewMode = () => {
    setPreviewMode(!previewMode);
  };

  // Renderização do conteúdo de cada etapa
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <StoreBasicInfoForm 
            slugError={slugError}
            setSlugError={setSlugError}
            checking={checking}
            setChecking={setChecking}
          />
        );
      case 1:
        return <ProductsForm />;
      case 2:
        return <PaymentConfigForm />;
      case 3:
        return (
          <StorePageForm 
            previewMode={previewMode} 
            togglePreviewMode={togglePreviewMode} 
          />
        );
      default:
        return 'Etapa desconhecida';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Criar Nova Loja
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure sua loja online em poucos passos simples
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit}>
            {/* Stepper */}
            <StoreFormStepper activeStep={activeStep} />

            {/* Conteúdo da etapa atual */}
            <Box sx={{ mt: 4, mb: 4 }}>
              {getStepContent(activeStep)}
            </Box>

            {/* Preview da loja (se ativado) */}
            {previewMode && (
              <Box sx={{ mt: 4 }}>
                <StorePreview formData={getValues()} />
              </Box>
            )}

            {/* Botões de navegação */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Voltar
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {onSaveDraft && (
                  <Button
                    onClick={handleSaveDraft}
                    variant="outlined"
                    color="secondary"
                  >
                    Salvar Rascunho
                  </Button>
                )}

                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                  >
                    Criar Loja
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    size="large"
                  >
                    Próximo
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Paper>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StoreFormContainer;
